import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchCrmCustomers, type CrmCustomer } from "@/services/adminCrm";

const API_URL = import.meta.env.VITE_API_URL || "";

export function CustomerSidebar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const selectedCustomerId = searchParams.get("customer") || "";

  const [search, setSearch] = useState("");
  const hideTest = searchParams.get("hideTest") === "true";

  function setHideTest(value: boolean) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set("hideTest", "true");
      } else {
        next.delete("hideTest");
      }
      return next;
    });
  }

  const { data: customers = [], isLoading } = useQuery<CrmCustomer[]>({
    queryKey: ["crm-customers"],
    queryFn: fetchCrmCustomers,
    staleTime: 30_000,
  });

  const totalUsers = customers.reduce((sum, c) => sum + c.userCount, 0);

  const filtered = customers.filter(c => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  async function toggleTest(customerId: string, currentIsTest: boolean) {
    try {
      const res = await fetch(`${API_URL}/admin/customers/${customerId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ isTest: !currentIsTest }),
      });
      if (!res.ok) throw new Error("Failed to update customer");
      toast.success(currentIsTest ? "Unmarked as test" : "Marked as test");
      queryClient.invalidateQueries({ queryKey: ["crm-customers"] });
    } catch (err: any) {
      toast.error(err.message);
    }
  }

  function selectCustomer(customerId: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (customerId) {
        next.set("customer", customerId);
      } else {
        next.delete("customer");
      }
      return next;
    });
  }

  return (
    <aside className="w-64 shrink-0 border-r bg-background flex flex-col h-full">
      {/* Back to dashboard */}
      <Link
        to="/recruit-admin/dashboard"
        className="flex items-center gap-1.5 px-4 py-2.5 text-xs text-muted-foreground hover:text-foreground border-b transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Admin Dashboard
      </Link>

      {/* Header */}
      <div className="p-4 border-b">
        <h2 className="font-semibold text-sm mb-3">Customers</h2>
        <Input
          placeholder="Search customers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="h-8 text-sm"
        />
      </div>

      {/* Hide test toggle */}
      <div className="px-4 py-2 border-b">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox
            checked={hideTest}
            onCheckedChange={checked => setHideTest(checked === true)}
            id="hide-test"
          />
          <span className="text-xs text-muted-foreground" id="hide-test">
            Hide test customers
          </span>
        </label>
      </div>

      {/* List */}
      <nav className="flex-1 overflow-y-auto py-1">
        {isLoading ? (
          <div className="px-4 py-3 text-xs text-muted-foreground">Loading...</div>
        ) : (
          <>
            {/* All Users item */}
            <button
              className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between hover:bg-muted/50 transition-colors ${
                !selectedCustomerId ? "bg-muted font-medium" : ""
              }`}
              onClick={() => selectCustomer("")}
            >
              <span>All Users</span>
              <span className="text-xs text-muted-foreground tabular-nums">{totalUsers}</span>
            </button>

            {/* Customer items */}
            {filtered.map(customer => {
              const isSelected = selectedCustomerId === customer.customerId;
              const isTest = customer.isTest;

              return (
                <div
                  key={customer.customerId}
                  className={`group flex items-center hover:bg-muted/50 transition-colors ${
                    isSelected ? "bg-muted font-medium" : ""
                  } ${isTest && hideTest ? "opacity-40" : isTest ? "opacity-60" : ""}`}
                >
                  <button
                    className="flex-1 text-left px-4 py-2 text-sm flex items-center justify-between min-w-0"
                    onClick={() => selectCustomer(customer.customerId)}
                  >
                    <span className="truncate pr-2">{customer.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      {customer.userCount}
                    </span>
                  </button>
                  <button
                    className={`shrink-0 p-1 mr-2 rounded hover:bg-muted transition-colors ${
                      isTest
                        ? "text-amber-500"
                        : "text-muted-foreground/30 opacity-0 group-hover:opacity-100"
                    }`}
                    onClick={e => {
                      e.stopPropagation();
                      toggleTest(customer.customerId, isTest);
                    }}
                    title={isTest ? "Unmark as test" : "Mark as test"}
                  >
                    <FlaskConical className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}

            {filtered.length === 0 && search && (
              <div className="px-4 py-3 text-xs text-muted-foreground">No customers match.</div>
            )}
          </>
        )}
      </nav>
    </aside>
  );
}
